import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bold, Italic, Image, List, ListOrdered } from 'lucide-react';
import { 
  IInstructionComponent, 
  IFormControlChange
} from '../df-form-preview-interfaces';

interface DfFormInstructionProps {
  id: string;
  properties: IInstructionComponent;
  mode?: 'edit' | 'preview' | 'test';
  formValue?: any; // Add formValue prop to receive data from formValues
  onValueChange?: (change: IFormControlChange) => void;
  className?: string;
  hideLabel?: boolean;
}

export const DfFormInstruction: React.FC<DfFormInstructionProps> = ({
  id,
  properties,
  mode = 'preview',
  formValue,
  onValueChange,
  className = '',
  hideLabel = false
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [listStyle, setListStyle] = useState<'none' | 'numbers' | 'bullets' | 'alpha'>(
    properties.validation?.listStyle || 'numbers'
  );
  const [selectedColor, setSelectedColor] = useState<string>('#000000');
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUserInputRef = useRef(false);
  const isEditable = mode === 'edit';

  // Track previous properties to detect actual changes
  const prevPropsRef = useRef<{ instructions: string[]; listStyle: string }>({ instructions: [], listStyle: 'numbers' });
  const prevModeRef = useRef<string>(mode);
  
  // Initialize content from properties - update when properties change or mode changes
  useEffect(() => {
    // CRITICAL: Check formValue first (from API/formValues), then fallback to properties
    // Handle different data formats for instructions
    let propsInstructions: string[] = [];
    
    // Priority 1: Check formValue (from API response or formValues state)
    if (formValue !== undefined && formValue !== null) {
      if (typeof formValue === 'object' && formValue.instructions) {
        // If formValue is an object with instructions property
        if (Array.isArray(formValue.instructions)) {
          propsInstructions = formValue.instructions;
        } else if (typeof formValue.instructions === 'string') {
          propsInstructions = [formValue.instructions];
        }
      } else if (Array.isArray(formValue)) {
        propsInstructions = formValue;
      } else if (typeof formValue === 'string') {
        propsInstructions = [formValue];
      }
    }
    
    // Priority 2: Fallback to properties.basic.instructions if formValue is empty
    if (propsInstructions.length === 0 && properties.basic?.instructions) {
      if (Array.isArray(properties.basic.instructions)) {
        propsInstructions = properties.basic.instructions;
      } else if (typeof properties.basic.instructions === 'string') {
        // If it's a string, try to parse it or use it as single instruction
        propsInstructions = [properties.basic.instructions];
      }
    }
    
    const propsListStyle = properties.validation?.listStyle || 'numbers';
    
    // Check if properties have actually changed
    const propsChanged = 
      JSON.stringify(propsInstructions) !== JSON.stringify(prevPropsRef.current.instructions) ||
      propsListStyle !== prevPropsRef.current.listStyle;
    
    // Check if mode changed (e.g., switching from preview to edit)
    const modeChanged = mode !== prevModeRef.current;
    
    // Update if properties changed or mode changed (to reload data when switching back to edit)
    if (propsChanged || modeChanged) {
      const hasHtml = propsInstructions.some(inst => typeof inst === 'string' && /<[^>]+>/.test(inst));
      let expectedContent = '';
      
      if (propsInstructions.length > 0) {
        if (hasHtml) {
          expectedContent = propsInstructions.join('');
        } else {
          const listTag = propsListStyle === 'numbers' ? 'ol' : propsListStyle === 'bullets' ? 'ul' : 'div';
          const listItems = propsInstructions
            .filter(inst => inst && typeof inst === 'string')
            .map(inst => `<li>${inst.replace(/\n/g, '<br>')}</li>`)
            .join('');
          
          if (listTag === 'div') {
            expectedContent = propsInstructions
              .filter(inst => inst && typeof inst === 'string')
              .map(inst => `<div>${inst.replace(/\n/g, '<br>')}</div>`)
              .join('');
          } else {
            expectedContent = `<${listTag}>${listItems}</${listTag}>`;
          }
        }
      }
      
      // Update state
      setListStyle(propsListStyle);
      setHtmlContent(expectedContent);
      
      // Update editor content when switching to edit mode or when properties change
      if (isEditable && editorRef.current) {
        // When switching to edit mode, always load from properties
        // When properties change, update if content is different
        if (modeChanged || (propsChanged && editorRef.current.innerHTML.trim() !== expectedContent.trim())) {
          editorRef.current.innerHTML = expectedContent;
        }
      }
      
      // Update previous refs
      prevPropsRef.current = { instructions: propsInstructions, listStyle: propsListStyle };
      prevModeRef.current = mode;
    }
  }, [formValue, properties.basic?.instructions, properties.validation?.listStyle, mode, isEditable]);

  // Sync htmlContent to editor when it changes (but not on user input)
  // This is mainly for when properties change externally
  useEffect(() => {
    if (isEditable && editorRef.current) {
      const currentEditorContent = editorRef.current.innerHTML.trim();
      const htmlContentTrimmed = htmlContent.trim();
      
      // Only update if content is different and it's not from user input
      if (htmlContentTrimmed !== currentEditorContent && !isUserInputRef.current) {
        editorRef.current.innerHTML = htmlContent;
      }
    }
    
    // Reset user input flag after a delay to allow for property updates
    if (isUserInputRef.current) {
      const timeout = setTimeout(() => {
        isUserInputRef.current = false;
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [htmlContent, isEditable]);

  // Handle content change - always save when content changes
  const handleContentChange = useCallback(() => {
    if (!editorRef.current) return;
    
    isUserInputRef.current = true;
    const content = editorRef.current.innerHTML.trim();
    
    // Always save the current content, even if it appears unchanged
    // This ensures data is persisted when user types and saves quickly
    const htmlInstructions = content ? [content] : [];
    
    // Always call onValueChange to ensure data is saved to field state
    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: {
          instructions: htmlInstructions,
          listStyle
        },
        isValid: true,
        errors: {}
      };
      onValueChange(change);
    }
    
    // Update state only if content actually changed (to avoid unnecessary re-renders)
    if (content !== htmlContent) {
      setHtmlContent(content);
    }
  }, [id, listStyle, onValueChange, htmlContent]);

  // Save on blur to ensure data is persisted when user leaves the editor
  const handleBlur = useCallback(() => {
    if (!editorRef.current) return;
    
    const content = editorRef.current.innerHTML.trim();
    const htmlInstructions = content ? [content] : [];
    
    // Always save on blur to ensure persistence
    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: {
          instructions: htmlInstructions,
          listStyle
        },
        isValid: true,
        errors: {}
      };
      onValueChange(change);
    }
    
    // Update state to match
    setHtmlContent(content);
    isUserInputRef.current = false;
  }, [id, listStyle, onValueChange]);

  // Format text command
  const executeCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  }, [handleContentChange]);

  // Handle bold
  const handleBold = useCallback(() => {
    executeCommand('bold');
  }, [executeCommand]);

  // Handle italic
  const handleItalic = useCallback(() => {
    executeCommand('italic');
  }, [executeCommand]);

  // Handle color
  const handleColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setSelectedColor(color);
    executeCommand('foreColor', color);
  }, [executeCommand]);

  // Handle list style change
  const handleListStyleChange = useCallback((style: 'none' | 'numbers' | 'bullets' | 'alpha') => {
    setListStyle(style);
    
    if (!editorRef.current) return;
    
    // Save current selection
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    // Get current content
    let currentContent = editorRef.current.innerHTML;
    
    // Convert existing lists to the new format
    if (style === 'numbers') {
      // Convert ul to ol
      currentContent = currentContent.replace(/<ul>/gi, '<ol>').replace(/<\/ul>/gi, '</ol>');
      // If no list exists, wrap content in ol
      if (!currentContent.includes('<ol>') && !currentContent.includes('<ul>')) {
        const lines = currentContent.split(/<br\s*\/?>/i).filter(line => line.trim());
        if (lines.length > 0) {
          currentContent = '<ol>' + lines.map(line => `<li>${line}</li>`).join('') + '</ol>';
        }
      }
    } else if (style === 'bullets') {
      // Convert ol to ul
      currentContent = currentContent.replace(/<ol>/gi, '<ul>').replace(/<\/ol>/gi, '</ul>');
      // If no list exists, wrap content in ul
      if (!currentContent.includes('<ol>') && !currentContent.includes('<ul>')) {
        const lines = currentContent.split(/<br\s*\/?>/i).filter(line => line.trim());
        if (lines.length > 0) {
          currentContent = '<ul>' + lines.map(line => `<li>${line}</li>`).join('') + '</ul>';
        }
      }
    } else {
      // Remove list formatting
      currentContent = currentContent.replace(/<\/?[ou]l>/gi, '').replace(/<li>/gi, '<div>').replace(/<\/li>/gi, '</div>');
    }
    
    // Update editor content
    editorRef.current.innerHTML = currentContent;
    setHtmlContent(currentContent);
    
    // Restore selection if possible
    if (range && selection) {
      try {
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e) {
        // Ignore selection errors
      }
    }
    
    // Trigger change
    handleContentChange();
    
    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: {
          instructions: currentContent ? [currentContent] : [],
          listStyle: style
        },
        isValid: true,
        errors: {}
      };
      onValueChange(change);
    }
  }, [id, handleContentChange, onValueChange]);

  // Handle image upload
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      if (imageUrl && editorRef.current) {
        // Insert image at cursor position
        const img = document.createElement('img');
        img.src = imageUrl;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);
        } else {
          editorRef.current.appendChild(img);
        }
        
        handleContentChange();
      }
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleContentChange]);

  // Handle paste events to clean up pasted content
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    handleContentChange();
  }, [handleContentChange]);

  // Check if command is active
  const isCommandActive = useCallback((command: string): boolean => {
    return document.queryCommandState(command);
  }, []);

  return (
    <div className={`form-group instruction-group ${className}`}>
      {!hideLabel && (
        <label htmlFor={id} className="instruction-label">
          {'Instructions'}
        </label>
      )}

      {/* Rich Text Editor - Only show in edit mode */}
      {isEditable ? (
        <div className="instruction-rich-editor">
          {/* Toolbar */}
          <div className="instruction-toolbar">
            <div className="toolbar-group">
              <button
                type="button"
                className={`toolbar-btn ${isCommandActive('bold') ? 'active' : ''}`}
                onClick={handleBold}
                title="Bold"
                aria-label="Bold"
              >
                <Bold size={16} />
              </button>
              <button
                type="button"
                className={`toolbar-btn ${isCommandActive('italic') ? 'active' : ''}`}
                onClick={handleItalic}
                title="Italic"
                aria-label="Italic"
              >
                <Italic size={16} />
              </button>
            </div>
            
            <div className="toolbar-separator" />
            
            <div className="toolbar-group">
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={selectedColor}
                  onChange={handleColorChange}
                  className="color-picker-input"
                  title="Text Color"
                />
                <label className="color-picker-label" title="Text Color">
                  <span style={{ color: selectedColor }}>A</span>
                </label>
              </div>
            </div>
            
            <div className="toolbar-separator" />
            
            <div className="toolbar-group">
              <button
                type="button"
                className={`toolbar-btn ${listStyle === 'bullets' ? 'active' : ''}`}
                onClick={() => handleListStyleChange('bullets')}
                title="Bullet List"
                aria-label="Bullet List"
              >
                <List size={16} />
              </button>
              <button
                type="button"
                className={`toolbar-btn ${listStyle === 'numbers' ? 'active' : ''}`}
                onClick={() => handleListStyleChange('numbers')}
                title="Numbered List"
                aria-label="Numbered List"
              >
                <ListOrdered size={16} />
              </button>
            </div>
            
            <div className="toolbar-separator" />
            
            <div className="toolbar-group">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Insert Image"
                aria-label="Insert Image"
              >
                <Image size={16} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Editor */}
          <div
            ref={editorRef}
            contentEditable
            className="instruction-editor-content"
            onInput={handleContentChange}
            onBlur={handleBlur}
            onPaste={handlePaste}
            data-placeholder="Enter instructions (press Enter for a new line)..."
            suppressContentEditableWarning
          />
        </div>
      ) : (
        /* Preview Mode - Show formatted content */
        <div className={`instruction-preview-wrapper ${listStyle === 'numbers' ? 'instruction-list-numbers' : listStyle === 'bullets' ? 'instruction-list-bullets' : ''}`}>
          {htmlContent ? (
            <div 
              className="instruction-preview-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <div className="instruction-empty">
              <p>No instructions provided</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DfFormInstruction;

